// Offline acoustic analysis of a clip: spectrogram columns + F1/F2 per control frame, and DTW comparison.
import { formantsOf } from '../vocalTract/voiceTracker';
import { SAMPLE_RATE } from './samuelCore';

const SPF = 512;

// Magnitude spectrogram (0..4 kHz, 96 bins) with one column per control frame.
export const analyzeClip = (wav) => {
  const n = Math.ceil(wav.length / SPF), N = 1024, BINS = 96, maxHz = 4000;
  const cols = [], formants = [];
  const win = new Float32Array(N).map((_, i) => 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1)));
  const fr = new Float32Array(N);
  for (let f = 0; f < n; f++) {
    const c = f * SPF + SPF / 2 - N / 2;
    let e = 0;
    for (let i = 0; i < N; i++) { const v = wav[c + i] || 0; fr[i] = v * win[i]; e += v * v; }
    const col = new Float32Array(BINS);
    for (let b = 0; b < BINS; b++) {
      const hz = ((b + 0.5) / BINS) * maxHz, w = (2 * Math.PI * hz) / SAMPLE_RATE;
      let re = 0, im = 0;
      for (let i = 0; i < N; i += 2) { re += fr[i] * Math.cos(w * i); im -= fr[i] * Math.sin(w * i); }
      col[b] = Math.log10(1e-6 + re * re + im * im);
    }
    cols.push(col);
    formants.push(e / N > 2e-5 ? formantsOf(fr, SAMPLE_RATE) : null);
  }
  let lo = Infinity, hi = -Infinity;
  cols.forEach((c) => c.forEach((v) => { lo = Math.min(lo, v); hi = Math.max(hi, v); }));
  return { cols, formants, lo: Math.max(lo, hi - 7), hi, maxHz };
};

// DTW over voiced formant frames (log-Hz) → 0..100 similarity.
export const compareFormants = (a, b) => {
  const pick = (fs) => fs.filter(Boolean).map((f) => [Math.log(f.f1), Math.log(f.f2)]);
  const A = pick(a), B = pick(b);
  if (A.length < 3 || B.length < 3) return null;
  const D = Array.from({ length: A.length + 1 }, () => new Float64Array(B.length + 1).fill(Infinity));
  D[0][0] = 0;
  for (let i = 1; i <= A.length; i++) for (let j = 1; j <= B.length; j++) {
    const d = Math.hypot(A[i - 1][0] - B[j - 1][0], (A[i - 1][1] - B[j - 1][1]) * 0.7);
    D[i][j] = d + Math.min(D[i - 1][j], D[i][j - 1], D[i - 1][j - 1]);
  }
  const avg = D[A.length][B.length] / (A.length + B.length);
  return Math.round(Math.max(0, Math.min(100, 100 - avg * 260)));
};

// Mean F1/F2 of the voiced part (for the trapezoid dot).
export const meanFormants = (fs) => {
  const v = fs.filter(Boolean);
  if (!v.length) return null;
  return { f1: v.reduce((s, f) => s + f.f1, 0) / v.length, f2: v.reduce((s, f) => s + f.f2, 0) / v.length };
};
